import { orderBy, query, where } from 'firebase/firestore';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useAuth, usePrincipal } from '@/lib/auth/use-auth';
import { useUsers } from '@/lib/directory/use-users';
import { useCollection } from '@/lib/firestore/hooks';
import { assetsCol, contactsCol, contractsCol } from '@/lib/firestore/refs';
import { SpecialHandlingNotes } from '@/lib/records/special-handling-banner';
import { Button, EmptyState, Select, Skeleton, StateBadge, Tabs, TextArea, toast } from '@/lib/ui';
import { formatDate } from '@/lib/utils/format';
import { activeSpecialHandling } from '@shared/crm';
import type { Asset, Company, CompanyActivity, Contact, Contract } from '@shared/types';
import { AccountTeamTab } from './account-team-tab';
import { addCompanyNote, setCompanyStatus } from './api';
import { useChildCompanies, useCompany, useCompanyActivity } from './hooks';
import { SpecialHandlingEditor } from './special-handling';

/** Phase 3b pushes `cases` and `work-orders` tabs and registers their renderers here. */
export const COMPANY_TABS: { id: string; label: string }[] = [
  { id: 'overview', label: 'Overview' }, { id: 'contacts', label: 'Contacts' }, { id: 'contracts', label: 'Contracts & SLA' }, { id: 'assets', label: 'Assets' },
  { id: 'team', label: 'Account team' }, { id: 'activity', label: 'Activity' }, { id: 'children', label: 'Child companies' },
];
export const COMPANY_TAB_RENDERERS: Record<string, (company: Company) => ReactNode> = {};
const NOTE_TYPES: CompanyActivity['type'][] = ['note', 'call', 'email', 'meeting'];

export function CompanyDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { role } = useAuth();
  const me = usePrincipal();
  const { data: c, loading } = useCompany(id);
  const contacts = useCollection<Contact>(useMemo(() => query(contactsCol(), where('companyId', '==', id), orderBy('name')), [id]), [id]);
  const contracts = useCollection<Contract>(useMemo(() => query(contractsCol(), where('companyId', '==', id), orderBy('endDate')), [id]), [id]);
  const assets = useCollection<Asset>(useMemo(() => query(assetsCol(), where('companyId', '==', id), orderBy('name')), [id]), [id]);
  const children = useChildCompanies(id);
  const activity = useCompanyActivity(id);
  const { byId: usersById } = useUsers();
  const [tab, setTab] = useState('overview');
  const [note, setNote] = useState('');
  const [noteType, setNoteType] = useState<CompanyActivity['type']>('note');
  if (loading) return <Skeleton lines={8} />;
  if (!c) return <EmptyState title="Company not found" />;
  const isAdmin = role === 'admin';
  const counts: Record<string, number | undefined> = { contacts: contacts.data.length, contracts: contracts.data.length, assets: assets.data.length, team: c.accountTeam.length, children: children.data.length };
  const facts: [string, ReactNode][] = [
    ['Website', c.website || '—'], ['Domains', c.domains.join(', ') || '—'], ['Phone', c.phone || '—'],
    ['Address', c.address ? `${c.address.line1}, ${c.address.city} ${c.address.state} ${c.address.postalCode}` : '—'], ['Time zone', c.timezone],
    ['SLA policy', c.slaPolicyId ?? 'default (inherited)'], ['Primary contact', contacts.data.find((x) => x.id === c.primaryContactId)?.name ?? '—'],
    ['Parent company', c.parentCompanyId ? <Link to={`/crm/companies/${c.parentCompanyId}`}>{c.parentCompanyId}</Link> : '—'], ['Open cases', c.openCaseCount], ['Created', formatDate(c.createdAt)],
  ];
  const logNote = async () => {
    await addCompanyNote(c.id, noteType, note.trim(), me);
    setNote('');
    toast.success('Logged');
  };
  return (
    <>
      <div className="record-header">
        <div>
          <div className="record-number">{c.id} · {c.code}</div>
          <div className="page-title">{c.name}</div>
          <div className="record-meta"><StateBadge state={c.status} /> <span className="badge badge-blue">{c.tier}</span> · {c.type} · {c.industry || '—'} · Account manager: {c.accountManagerUid ? (usersById.get(c.accountManagerUid)?.name ?? c.accountManagerUid) : '—'}</div>
        </div>
        {isAdmin ? (
          <div className="record-actions">
            <Button onClick={() => navigate(`/crm/companies/${c.id}/edit`)}>Edit</Button>
            {c.status !== 'active' ? <Button variant="primary" onClick={() => setCompanyStatus(c.id, 'active', me)}>Activate</Button> : null}
            {c.status !== 'archived' ? <Button variant="danger" onClick={() => setCompanyStatus(c.id, 'archived', me)}>Archive</Button> : null}
          </div>
        ) : null}
      </div>
      <SpecialHandlingNotes notes={activeSpecialHandling(c)} />
      <Tabs tabs={COMPANY_TABS.map((t) => ({ ...t, count: counts[t.id] }))} activeId={tab} onChange={setTab} />
      {tab === 'overview' ? (
        <div className="two-col-layout">
          <div className="card"><div className="card-body">{facts.map(([k, v]) => <div key={k} className="settings-row"><div className="settings-label">{k}</div><div>{v}</div></div>)}</div></div>
          <SpecialHandlingEditor company={c} canEdit={isAdmin} />
        </div>
      ) : null}
      {tab === 'contacts' ? (
        <div className="card">
          <div className="card-header"><span className="card-title">Contacts</span><Button size="sm" variant="primary" onClick={() => navigate(`/crm/contacts?company=${c.id}&new=1`)}>Add contact</Button></div>
          <div className="card-body">
            {contacts.data.length === 0 ? <div className="empty-state">No contacts</div> : contacts.data.map((x) => (
              <div key={x.id} className="settings-row"><div><Link to={`/crm/contacts/${x.id}`}>{x.name}</Link>{x.isPrimary ? <> <span className="badge badge-green">Primary</span></> : null}<div className="settings-desc">{x.title} · {x.email} · {x.phone}</div></div></div>
            ))}
          </div>
        </div>
      ) : null}
      {tab === 'contracts' ? (
        <div className="card">
          <div className="card-header"><span className="card-title">Contracts</span>{isAdmin ? <Button size="sm" variant="primary" onClick={() => navigate(`/crm/contracts/new?company=${c.id}`)}>New contract</Button> : null}</div>
          <div className="card-body">
            {contracts.data.length === 0 ? <div className="empty-state">No contracts</div> : contracts.data.map((x) => (
              <div key={x.id} className="settings-row"><div><Link to={`/crm/contracts/${x.id}`}>{x.name}</Link> <StateBadge state={x.status} /><div className="settings-desc">{x.type} · {x.startDate} → {x.endDate} · {x.entitlements.supportHours} · SLA {x.slaPolicyId ?? 'inherited'}</div></div></div>
            ))}
          </div>
        </div>
      ) : null}
      {tab === 'assets' ? (
        <div className="card"><div className="card-body">
          {assets.data.length === 0 ? <div className="empty-state">No customer-owned assets</div> : assets.data.map((a) => <div key={a.id} className="settings-row"><div><Link to={`/assets/${a.id}`}>{a.tag}</Link> {a.name}<div className="settings-desc">{a.type} · {a.status}</div></div></div>)}
        </div></div>
      ) : null}
      {tab === 'team' ? <AccountTeamTab company={c} /> : null}
      {tab === 'activity' ? (
        <div className="card"><div className="card-body">
          <div className="form-row">
            <Select value={noteType} onChange={(e) => setNoteType(e.target.value as CompanyActivity['type'])} options={NOTE_TYPES.map((t) => ({ value: t, label: t }))} aria-label="Activity type" />
            <TextArea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Log a note, call, email or meeting…" rows={2} />
            <Button variant="primary" disabled={!note.trim()} onClick={logNote}>Log</Button>
          </div>
          {activity.data.map((a) => <div key={a.id} className="activity-item"><div className="activity-dot" /><div className="activity-content"><strong>{a.type}</strong> — {a.body}<div className="activity-time">{a.by.name} · {formatDate(a.at, { withTime: true })}</div></div></div>)}
        </div></div>
      ) : null}
      {tab === 'children' ? (
        <div className="card"><div className="card-body">
          {children.data.length === 0 ? <div className="empty-state">No child companies</div> : children.data.map((x) => <div key={x.id} className="settings-row"><Link to={`/crm/companies/${x.id}`}>{x.name}</Link><StateBadge state={x.status} /></div>)}
        </div></div>
      ) : null}
      {COMPANY_TAB_RENDERERS[tab]?.(c)}
    </>
  );
}
